import * as React from "react";
import { Translations } from "ui/Scripts/source/models/translations";
import { Base } from "ui/Scripts/source/framework/base";
import { TaskDynamicForm } from "../TaskDynamicForm/TaskDynamicForm";
import { ITaskEditTypeItem, TaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { ITaskDataRow, TaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { ITaskDataRowCol, TaskDataRowCol } from "ui/Scripts/source/models/task/taskDataRowCol";
import { ITaskTypeCol, TaskTypeCol } from "ui/Scripts/source/models/task/taskTypeCol";
import { TaskDataRowDataType } from "ui/Scripts/source/models/enums";
import { TaskDynamicFormContainer } from "../TaskDynamicFormContainer";
import { IdTitle } from "ui/Scripts/source/models/common/idTitle";

interface ITaskPageFilterDrawerTaskTypeParametersProp {
    taskTypes: ITaskEditTypeItem[];
    taskType: ITaskEditTypeItem;
    headerParameters: ITaskDataRow;
    formParameters: ITaskDataRow;
    onSetHeaderParameters: (taskDataRowCols: ITaskDataRowCol[]) => void;
    onSetFormParameters: (taskDataRowCols: ITaskDataRowCol[]) => void;
}

export class TaskPageFilterDrawerTaskTypeParameters extends React.Component<ITaskPageFilterDrawerTaskTypeParametersProp, {}> {
    static getNewTaskTypeCol = (typeCol: ITaskTypeCol, fieldSuffix: string, titleSuffix: string) => {
        const result = new TaskTypeCol(typeCol);
        result.field = typeCol.field + fieldSuffix;
        result.title = typeCol.title + titleSuffix;
        result.required = false;
        result.class = "col col-6";
        if (result.dataType === TaskDataRowDataType.String && (result.options && result.options.length > 0 || result.idOptions && result.idOptions.length > 0)) {
            result.defaultStr = "";
            result.dataType = TaskDataRowDataType.StringArray;
            if (result.options && result.options.length > 0) {
                result.idOptions = result.options.map(i => IdTitle.createIdTitle(i, i));
                result.options = [];
            }
            result.idOptions.unshift(IdTitle.createIdTitle("", `'${Translations.All}'`));
        } else if (result.dataType === TaskDataRowDataType.Boolean) {
            result.defaultStr = "";
            result.dataType = TaskDataRowDataType.String;
            result.idOptions.push(IdTitle.createIdTitle("", `'${Translations.All}'`));
            result.idOptions.push(IdTitle.createIdTitle("0", Translations.FormFalse));
            result.idOptions.push(IdTitle.createIdTitle("1", Translations.FormTrue));
        }
        return result;
    };

    static addTaskTypeColToFilterCols = (filterCols: ITaskTypeCol[], typeCol: ITaskTypeCol) => {
        if (!typeCol || !typeCol.useInFilter || !typeCol.isSaveDataType() || !typeCol.isSavedTaskDataField() || !TaskTypeCol.colCanBeUsedInFilter(typeCol)) return;
        if (typeCol.dataType === TaskDataRowDataType.Date || typeCol.dataType === TaskDataRowDataType.Time) {
            const startCol = TaskPageFilterDrawerTaskTypeParameters.getNewTaskTypeCol(typeCol, "_Start", "");
            filterCols.push(startCol);
            const endCol = TaskPageFilterDrawerTaskTypeParameters.getNewTaskTypeCol(typeCol, "_End", "");
            filterCols.push(endCol);
            if (typeCol.dataType === TaskDataRowDataType.Date) {
                const dynamicDateFilterSuffix = "_DynamicDateFilterId";
                startCol.readOnlyExpression = `'${typeCol.field + dynamicDateFilterSuffix}' !== '0' && '${typeCol.field + dynamicDateFilterSuffix}' !== ''`;
                startCol.clearExpression = `'${typeCol.field + dynamicDateFilterSuffix}' !== '0' && '${typeCol.field + dynamicDateFilterSuffix}' !== ''`;
                endCol.title = "&nbsp;";
                endCol.readOnlyExpression = `'${typeCol.field + dynamicDateFilterSuffix}' !== '0' && '${typeCol.field + dynamicDateFilterSuffix}' !== ''`;
                endCol.clearExpression = `'${typeCol.field + dynamicDateFilterSuffix}' !== '0' && '${typeCol.field + dynamicDateFilterSuffix}' !== ''`;
                const dynamicDateFilter = new TaskTypeCol();
                dynamicDateFilter.field = typeCol.field + dynamicDateFilterSuffix;
                dynamicDateFilter.title = "";
                dynamicDateFilter.class = "col col-12";
                dynamicDateFilter.defaultStr = "0";
                dynamicDateFilter.dataType = TaskDataRowDataType.String;
                dynamicDateFilter.idOptions = [
                    IdTitle.createIdTitle("0", `'${Translations.SpecifedTimeFrame}'`),
                    IdTitle.createIdTitle("4", Translations.CurrentCalendarMonth),
                    IdTitle.createIdTitle("5", Translations.CurrentAndPreviousCalendarMonth),
                    IdTitle.createIdTitle("6", Translations.CurrentCalendarHalfYear),
                    IdTitle.createIdTitle("7", Translations.CurrentAndPreviousCalendarHalfYear),
                    IdTitle.createIdTitle("1", Translations.CurrentCalendarYear),
                    IdTitle.createIdTitle("2", Translations.PreviousCalendarYear),
                    IdTitle.createIdTitle("3", Translations.CurrentAndPreviousCalendarYear)
                ];
                filterCols.push(dynamicDateFilter);
            }
        } else if (typeCol.dataType === TaskDataRowDataType.TimeSpan || typeCol.dataType === TaskDataRowDataType.Integer || typeCol.dataType === TaskDataRowDataType.Number) {
            filterCols.push(TaskPageFilterDrawerTaskTypeParameters.getNewTaskTypeCol(typeCol, "_Min", " - " + Translations.Min));
            filterCols.push(TaskPageFilterDrawerTaskTypeParameters.getNewTaskTypeCol(typeCol, "_Max", " - " + Translations.Max));
        } else {
            filterCols.push(TaskPageFilterDrawerTaskTypeParameters.getNewTaskTypeCol(typeCol, "", ""));
        }
    };

    static getNewTaskType = (taskTypes: ITaskEditTypeItem[], selectedTaskTypeIds: string[]): ITaskEditTypeItem => {
        if (selectedTaskTypeIds.length !== 1 || selectedTaskTypeIds[0] === Base.emptyGuid) return null;
        const taskType = taskTypes.find(i => i.id === selectedTaskTypeIds[0]);
        if (!taskType) return null;
        const result = new TaskEditTypeItem();
        for (const typeCol of taskType.headerCols) {
            this.addTaskTypeColToFilterCols(result.headerCols, typeCol);
        }
        for (const typeCol of taskType.formCols) {
            this.addTaskTypeColToFilterCols(result.formCols, typeCol);
        }
        return result;
    };

    static getNewTaskDataRowCol = (typeCol: ITaskTypeCol, dataCol: ITaskDataRowCol, fieldsObj: any) => {
        const result = new TaskDataRowCol(dataCol);
        const fieldsObjValue = fieldsObj ? fieldsObj[dataCol.field] : null;
        if (fieldsObjValue) {
            result.setTaskDataRowColValueFromDbValue(typeCol, fieldsObjValue);
        }
        if (!(typeCol.dataType === TaskDataRowDataType.String && typeCol.idOptions && typeCol.idOptions.length > 0)) {
            result.valueStr = TaskDataRowCol.getTaskDataRowColUiValue(typeCol, result.valueStr, result.valueNbr);
        }
        return result;
    };

    static getTaskDataRow = (taskTypeCols: ITaskTypeCol[], serializedFields: string): ITaskDataRow => {
        const result = new TaskDataRow();
        result.id = Base.getGuid();
        const fieldsObj = serializedFields ? JSON.parse(serializedFields) : null;
        for (const typeCol of taskTypeCols) {
            const dataCol = TaskDataRowCol.getDefaultValueTaskDataRowCol(typeCol);
            result.cols.push(TaskPageFilterDrawerTaskTypeParameters.getNewTaskDataRowCol(typeCol, dataCol, fieldsObj));
        }
        return result.cols.length > 0
            ? result
            : null;
    };

    static getSerializedFields(taskTypeCols: ITaskTypeCol[], taskDataRow: ITaskDataRow): string {
        if (!taskDataRow) return "";
        const fieldsObj = {};
        for (const col of taskDataRow.cols) {
            const typeCol = taskTypeCols.find(i => i.field === col.field);
            if (!typeCol) continue;
            if (typeCol.dataType === TaskDataRowDataType.Date ||
                typeCol.dataType === TaskDataRowDataType.Time ||
                typeCol.dataType === TaskDataRowDataType.TimeSpan ||
                typeCol.dataType === TaskDataRowDataType.Integer ||
                typeCol.dataType === TaskDataRowDataType.Number ||
                typeCol.dataType === TaskDataRowDataType.Boolean) {
                fieldsObj[col.field] = col.valueNbr ? col.valueNbr : null;
            } else {
                fieldsObj[col.field] = col.valueStr;
            }
        }
        return JSON.stringify(fieldsObj);
    }

    static getStateChangeFromTaskType = (taskType: ITaskEditTypeItem, selectedTaskTypeHeaderFields: string, selectedTaskTypeFields: string): any => {
        if (!taskType) {
            return {
                taskType: null,
                headerParameters: null,
                formParameters: null,
                selectedTaskTypeHeaderFields: null,
                selectedTaskTypeFields: null
            };
        }
        const headerParameters = TaskPageFilterDrawerTaskTypeParameters.getTaskDataRow(taskType.headerCols, selectedTaskTypeHeaderFields);
        const formParameters = TaskPageFilterDrawerTaskTypeParameters.getTaskDataRow(taskType.formCols, selectedTaskTypeFields);
        return {
            taskType,
            headerParameters,
            formParameters,
            selectedTaskTypeHeaderFields: TaskPageFilterDrawerTaskTypeParameters.getSerializedFields(taskType.headerCols, headerParameters),
            selectedTaskTypeFields: TaskPageFilterDrawerTaskTypeParameters.getSerializedFields(taskType.formCols, formParameters)
        };
    };

    static getFormParametersState = (state: { taskType: ITaskEditTypeItem, formParameters: ITaskDataRow }, taskDataRowCols: ITaskDataRowCol[]) => {
        if (!state.formParameters) return null;
        const formParameters = new TaskDataRow(state.formParameters);
        formParameters.cols = taskDataRowCols;
        return {
            formParameters,
            selectedTaskTypeFields: TaskPageFilterDrawerTaskTypeParameters.getSerializedFields(state.taskType.formCols, formParameters)
        };
    };

    static getExtraHeaderParametersState = (state: { taskType: ITaskEditTypeItem, headerParameters: ITaskDataRow }, taskDataRowCols: ITaskDataRowCol[]) => {
        if (!state.headerParameters) return null;
        const headerParameters = new TaskDataRow(state.headerParameters);
        headerParameters.cols = taskDataRowCols;
        return {
            headerParameters,
            selectedTaskTypeHeaderFields: TaskPageFilterDrawerTaskTypeParameters.getSerializedFields(state.taskType.headerCols, headerParameters),
        };
    };

    static getChangedStateAdterChangeTaskTypeIds = (props: { taskTypes: ITaskEditTypeItem[], selectedTaskTypeHeaderFields: string, selectedTaskTypeFields: string }, selectedTaskTypeIds: string[]) => {
        return {
            selectedTaskTypeIds,
            ...TaskPageFilterDrawerTaskTypeParameters.getStateChangeFromTaskType(TaskPageFilterDrawerTaskTypeParameters.getNewTaskType(props.taskTypes, selectedTaskTypeIds), props.selectedTaskTypeHeaderFields, props.selectedTaskTypeFields)
        };
    };

    static getClearedParametersState = (taskTypes: ITaskEditTypeItem[]) => {
        return TaskPageFilterDrawerTaskTypeParameters.getStateChangeFromTaskType(TaskPageFilterDrawerTaskTypeParameters.getNewTaskType(taskTypes, [taskTypes[0].id]), "", "");
    };

    render() {
        const props = this.props;
        if (!props.taskType || (!props.headerParameters && !props.formParameters)) return null;
        return (
            <div style={{ marginTop: "15px" }}>
                {!!props.headerParameters &&
                    <TaskDynamicFormContainer>
                        <TaskDynamicForm
                            taskTypeCols={props.taskType.headerCols}
                            taskDataRowCols={props.headerParameters.cols}
                            onRefreshCols={props.onSetHeaderParameters}
                        />
                    </TaskDynamicFormContainer>
                }
                {!!props.formParameters &&
                    <TaskDynamicFormContainer>
                        <TaskDynamicForm
                            taskTypeCols={props.taskType.formCols}
                            taskDataRowCols={props.formParameters.cols}
                            onRefreshCols={props.onSetFormParameters}
                        />
                    </TaskDynamicFormContainer>
                }
            </div>
        );
    }
}
